import styled from 'styled-components';
import {
  breakpoint,
  BodyText,
  ResponsiveTable,
  ColumnData,
  Link,
} from '@grays-npm/component-library';
import { Loader, Modal, ModalProps, useAuthData } from '../../../components';
import { ListingModalData, ListingModalTypeEnum } from './types';
import { useCMSData } from '../../../state/content-manager';
import { useQuery } from 'react-query';
import {
  getAccountSaleInvoice,
  GetAccountSaleInvoiceRequestType,
  InvoiceParams,
  viewPaymentDetails,
} from '../../../services/listings';
import { useState } from 'react';
import TagManager from 'react-gtm-module';
import { QUERY_KEYS } from 'utils/constants';

const TABLE_COLUMNS: ColumnData[] = [
  {
    label: 'Date',
    key: 'paymentDate',
    settings: {
      large: {
        showLabel: true,
      },
      small: {
        order: 0,
        showLabel: false,
      },
    },
  },
  {
    label: 'Invoice',
    key: 'invoiceParams',
    isBold: false,
    settings: {
      large: {
        showLabel: false,
      },
      small: {
        order: 4,
        showLabel: false,
      },
    },
  },
  {
    label: 'Payment Details',
    key: 'details',
    settings: {
      large: {
        showLabel: true,
      },
      small: {
        order: 2,
        showLabel: false,
      },
    },
  },
  {
    label: 'Qty',
    key: 'quantity',
    settings: {
      large: {
        showLabel: true,
      },
      small: {
        order: 3,
        showLabel: true,
      },
    },
    type: 'number',
  },
  {
    label: 'Amount',
    key: 'amount',
    isBold: true,
    settings: {
      large: {
        showLabel: true,
      },
      small: {
        order: 1,
        showLabel: false,
      },
    },
    type: 'number',
  },
];

const Text = styled(BodyText)`
  margin: 0 0 ${({ theme }) => theme.spacings[4].rem};

  ${breakpoint('sm')`
    margin: ${({ theme: { spacings } }) =>
      `0 ${spacings[5].rem} ${spacings[5].rem} 0`};
  `}
`;

type ViewPaymentDetailsModalProps = Omit<ModalProps, 'title'> &
  ListingModalData<ListingModalTypeEnum.ViewPaymentDetails>;

export const ViewPaymentDetailsModal: React.FC<ViewPaymentDetailsModalProps> =
  ({ isOpen, lotSku, onClose }) => {
    const { token } = useAuthData();
    const cmsData = useCMSData();
    const [pdfData, setPdfData] =
      useState<GetAccountSaleInvoiceRequestType | null>(null);

    const viewPaymentDetailsQuery = useQuery([QUERY_KEYS.VIEW_PAYMENT_DETAILS], () => {
      const viewPaymentDetailsRequest = { LotSku: lotSku };
      return viewPaymentDetails(token, viewPaymentDetailsRequest);
    });

    useQuery(
      [QUERY_KEYS.GET_ACCOUNT_SALE_INVOICE, pdfData, token],
      () => pdfData && getAccountSaleInvoice(token, pdfData),
      {
        enabled: !!pdfData,
        onSuccess: () => {
          setPdfData(null);
        },
      }
    );

    const onDownloadPDF = ({
      managingOfficeId,
      saleNo,
      vendorNo,
    }: InvoiceParams) => {
      setPdfData({
        managingOfficeId: managingOfficeId.toString(),
        saleNo: saleNo.toString(),
        vendorNo: vendorNo.toString(),
      });
      TagManager.dataLayer({
        dataLayer: {
          event: 'sellersPaymentDetailsModal-downloadPDF',
        },
      });
    };

    const { body, title } = cmsData.modals.paymentDetails;
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={title} modalSize="lg">
        <Text size="lg">{body}</Text>
        <Loader query={viewPaymentDetailsQuery}>
          {viewPaymentDetailsQuery.data && (
            <ResponsiveTable
              columnData={TABLE_COLUMNS}
              data={viewPaymentDetailsQuery.data.map(
                ({ invoiceParams, ...row }) => ({
                  ...row,
                  ...(invoiceParams && {
                    invoiceParams: {
                      label: 'Download PDF',
                      onClick: () => onDownloadPDF(invoiceParams),
                    } as Link,
                  }),
                })
              )}
            />
          )}
        </Loader>
      </Modal>
    );
  };
